import { useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'
import { Badge } from '@wartek-id/badge'
import type { DialogProps } from '@wartek-id/dialog-v1'
import {
  Dialog,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogCloseButton,
  DialogFooter,
} from '@wartek-id/dialog-v1'
import { Icon } from '@wartek-id/icon-v2'

import { assetUrl } from 'utils/url'
import { SchoolItem } from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardPenetapanPredikatKinerjaTahunan/SchoolItem'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { SchoolItemProps } from 'app/KinerjaPage/pages/PelaksanaanV2/components/CardPenetapanPredikatKinerjaTahunan/SchoolItem'
import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'
import type { SchoolBadgeStatus } from './type'

type CardPenetapanPredikatKinerjaTahunanProps = {
  className?: string
  hasPKO: boolean
  isAllSent: boolean
  itemsToSend: number
  schools: Array<PelaksanaanSchool>
}

export function CardPenetapanPredikatKinerjaTahunan({
  className,
  hasPKO,
  isAllSent,
  itemsToSend,
  schools,
}: CardPenetapanPredikatKinerjaTahunanProps) {
  const [isDialogOpen, setDialogOpen] = useState(false)
  const { periodYear } = usePeriodContext()

  const schoolItems = useMemo(
    () =>
      schools.map((school) => ({
        itemsToSend: school.predikatReadyTotal,
        name: school.schoolName,
        npsn: school.npsn,
        role: school.role,
        status: generateBadgeStatus(
          school.predikatKinerjaOrganisasiReady,
          school.predikatAnnualCompleted,
          school.predikatReadyTotal
        ),
      })),
    [schools]
  )

  const status: SchoolBadgeStatus = useMemo(
    () => generateBadgeStatus(hasPKO, isAllSent, itemsToSend),
    [hasPKO, isAllSent, itemsToSend]
  )

  const ctaText =
    status === 'READY_TO_SEND' ? 'Lakukan Penetapan' : 'Cek Penetapan'
  const ctaColor = status === 'READY_TO_SEND' ? 'blue' : 'white'
  const isCtaDisabled = status === 'UNAVAILABLE'

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }
  const handleOpenDialog = () => {
    setDialogOpen(true)
  }

  return (
    <>
      <Card className={'p-6 !flex-row space-x-6 items-center ' + className}>
        <img
          src={assetUrl('/illustrations/img-assessment-subject-book.png')}
          alt=""
          width={64}
          height={64}
          className="flex-none"
        />

        <div className="w-full flex-1 space-y-2">
          <Text variant="heading-lg" className="!text-black" as="h2">
            Penetapan Predikat Kinerja (Tahunan - {periodYear})
          </Text>

          <StatusBadge status={status} itemsToSend={itemsToSend} />
        </div>

        <Button
          aria-label={`${ctaText} ${name}`}
          color={ctaColor}
          disabled={isCtaDisabled}
          size="lg"
          onClick={handleOpenDialog}
        >
          {ctaText}
        </Button>
      </Card>

      <DialogSchools
        isOpen={isDialogOpen}
        schoolItems={schoolItems}
        onClose={handleCloseDialog}
      />
    </>
  )
}

type StatusBadgeProps = {
  status: SchoolBadgeStatus
  itemsToSend: number
}
function StatusBadge({ status, itemsToSend }: StatusBadgeProps) {
  switch (status) {
    case 'ALL_SENT':
      return <Badge variant="success">Semua Predikat sudah dikirim</Badge>
    case 'NOT_READY_TO_SEND':
      return (
        <Badge className="!bg-gray-20">
          Belum ada Predikat yang siap dikirim
        </Badge>
      )
    case 'READY_TO_SEND':
      return (
        <Badge variant="warning">{itemsToSend} Predikat siap dikirim</Badge>
      )
    default:
      return null
  }
}

type DialogSchoolsProps = Omit<DialogProps, 'size' | 'children'> & {
  schoolItems: Array<SchoolItemProps>
}

function DialogSchools({ schoolItems, ...dialogProps }: DialogSchoolsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0,
  })

  return (
    <Dialog {...dialogProps} size="sm">
      <DialogOverlay />
      <DialogContent
        aria-labelledby="card-penyesuaian-pelaksaanaan-dialog-title"
        className="overflow-hidden !my-auto"
      >
        <DialogCloseButton />

        <div className="flex flex-col max-h-screen">
          <DialogHeader borderless title="Pilih satuan pendidikan" />

          <div
            className="p-6 flex-grow-1 overflow-y-auto max-h-full"
            ref={scrollContainerRef}
          >
            <ul className="space-y-4">
              {schoolItems.map((school) => (
                <SchoolItem
                  itemsToSend={school.itemsToSend}
                  key={school.npsn}
                  name={school.name}
                  npsn={school.npsn}
                  role={school.role}
                  status={school.status}
                />
              ))}
            </ul>

            {/* Sentinel element to detect inView */}
            <div className="w-full bg-red-70" ref={ref} />

            {/* Scroll to bottom button */}
            {!inView && (
              <Button
                className="!rounded-full space-x-1 sticky -bottom-1 mx-auto z-10"
                style={{
                  boxShadow: 'var(--elevation-04)',
                }}
                onClick={() => {
                  const scrollContainerElm = scrollContainerRef?.current

                  if (scrollContainerElm) {
                    scrollContainerElm.scrollTo({
                      left: 0,
                      top: scrollContainerElm.scrollHeight,
                      behavior: 'smooth',
                    })
                  }
                }}
              >
                <Icon color="inverse">arrow_downward</Icon>
                <span>Geser ke bawah</span>
              </Button>
            )}
          </div>

          <DialogFooter borderless className="!p-6 ">
            <Button
              color="white"
              fullWidth
              onClick={() => dialogProps.onClose?.()}
            >
              Kembali
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function generateBadgeStatus(
  hasPKO: boolean,
  isAllSent: boolean,
  itemsToSend: number
): SchoolBadgeStatus {
  if (isAllSent) {
    return 'ALL_SENT'
  } else if (hasPKO && itemsToSend > 0) {
    return 'READY_TO_SEND'
  } else if (hasPKO && itemsToSend <= 0) {
    return 'NOT_READY_TO_SEND'
  } else {
    return 'UNAVAILABLE'
  }
}
